import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './AIAssistant.css';

function backendUrl() {
  if (process.env.REACT_APP_BACKEND_URL) return process.env.REACT_APP_BACKEND_URL.replace(/\/$/, '');
  const { hostname, protocol } = window.location;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return ['3001', '3010'].includes(window.location.port) ? window.location.origin : 'http://localhost:8001';
  if (/^(192\.168\.|10\.|172\.)/.test(hostname)) return `${protocol}//${hostname}:8001`;
  return `${protocol}//${hostname}`;
}
function token() {
  for (const key of ['vendorAuth', 'adminAuth']) {
    try { const v = JSON.parse(localStorage.getItem(key) || 'null'); if (v?.token) return v.token; } catch (_) {}
  }
  return localStorage.getItem('agentToken') || null;
}
function role() {
  try { if (JSON.parse(localStorage.getItem('vendorAuth') || 'null')?.token) return 'vendor'; } catch (_) {}
  try { if (JSON.parse(localStorage.getItem('adminAuth') || 'null')?.token) return 'admin'; } catch (_) {}
  if (localStorage.getItem('agentToken')) return 'agent';
  return 'customer';
}
function clientId() {
  let id = localStorage.getItem('shopi_client_id');
  if (!id) { id = `${Date.now()}-${Math.random().toString(36).slice(2)}`; localStorage.setItem('shopi_client_id', id); }
  return id;
}
function authHeaders(json = true) {
  const h = {};
  if (json) h['Content-Type'] = 'application/json';
  const t = token(); if (t) h.Authorization = `Bearer ${t}`;
  return h;
}
function safeUrl(value) {
  try { return new URL(value, window.location.origin).toString(); } catch (_) { return ''; }
}
function mediaUrl(value) {
  const raw = String(value || '');
  if (raw.startsWith('/api/uploads/')) return `${backendUrl()}${raw}`;
  return safeUrl(raw);
}
function readJsonStorage(keys) {
  for (const key of keys) {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      const value = JSON.parse(raw);
      if (value && typeof value === 'object') return value;
    } catch (_) {}
  }
  return null;
}

function pageContext() {
  const params = new URLSearchParams(window.location.search);
  const selectedLocation = readJsonStorage(['userSelectedLocation', 'shopi_location', 'shopimax_location']) || {};
  const vendor = readJsonStorage(['vendorAuth'])?.vendor || readJsonStorage(['vendorData']) || {};
  const store = readJsonStorage(['shopi_current_store', 'currentStore']) || {};
  const product = readJsonStorage(['shopi_current_product', 'currentProduct']) || {};
  return {
    path: window.location.pathname,
    query: params.toString(),
    title: document.title,
    search: params.get('search') || params.get('q') || '',
    category: params.get('category') || '',
    store_id: localStorage.getItem('shopi_current_store_id') || localStorage.getItem('currentStoreId') || store.id || store._id || null,
    store_name: store.name || '',
    product_id: localStorage.getItem('shopi_current_product_id') || localStorage.getItem('currentProductId') || product.id || product._id || null,
    product_name: product.name || '',
    vendor_id: vendor.id || vendor._id || null,
    location_label: selectedLocation.name || selectedLocation.label || '',
    location_source: selectedLocation.source || 'selected'
  };
}

function renderInline(text, keyPrefix = 'x') {
  const normalized = String(text || '')
    .replace(/\\([*#_`~.\-])/g, '$1');
  const parts = normalized.split(/(\*\*[^*]+\*\*|__[^_]+__|\*[^*]+\*|_[^_]+_|`[^`]+`|~~[^~]+~~|\[[^\]]+\]\([^)]+\))/g);
  return parts.map((part, i) => {
    if (/^\*\*[^*]+\*\*$/.test(part) || /^__[^_]+__$/.test(part)) return <strong key={`${keyPrefix}-b${i}`}>{part.slice(2, -2)}</strong>;
    if (/^\*[^*]+\*$/.test(part) || /^_[^_]+_$/.test(part)) return <em key={`${keyPrefix}-i${i}`}>{part.slice(1, -1)}</em>;
    if (/^`[^`]+`$/.test(part)) return <code key={`${keyPrefix}-c${i}`}>{part.slice(1, -1)}</code>;
    if (/^~~[^~]+~~$/.test(part)) return <del key={`${keyPrefix}-d${i}`}>{part.slice(2, -2)}</del>;
    const link = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (link) return <a key={`${keyPrefix}-a${i}`} href={link[2]} target="_blank" rel="noopener noreferrer">{link[1]}</a>;
    return part;
  });
}

function RichText({ text }) {
  const normalizedText = String(text || '')
    .replace(/\\([*#_`~.\-])/g, '$1');
  const lines = normalizedText.replace(/\r\n/g, '\n').trim().split('\n');
  const out = [];
  let list = [];
  let ordered = false;

  const flush = () => {
    if (!list.length) return;
    const Tag = ordered ? 'ol' : 'ul';
    out.push(
      <Tag key={`l${out.length}`} className="shopi-rich-list">
        {list.map((x, i) => <li key={i}>{renderInline(x, `l${i}`)}</li>)}
      </Tag>
    );
    list = [];
    ordered = false;
  };

  lines.forEach((line, i) => {
    const s = line.trim();
    if (!s) { flush(); return; }

    const numbered = s.match(/^\d+\.\s+(.+)$/);
    const bullet = s.match(/^[-*•]\s+(.+)$/);

    if (numbered) {
      if (list.length && !ordered) flush();
      ordered = true;
      list.push(numbered[1]);
      return;
    }

    if (bullet) {
      if (list.length && ordered) flush();
      ordered = false;
      list.push(bullet[1]);
      return;
    }

    flush();

    if (/^([-*_])\s*\1\s*\1[\s\1]*$/.test(s)) {
      out.push(<hr key={i} className="shopi-rich-divider" />);
      return;
    }

    const quote = s.match(/^>\s+(.+)$/);
    if (quote) {
      out.push(<blockquote key={i}>{renderInline(quote[1], `q${i}`)}</blockquote>);
      return;
    }

    const h = s.match(/^#{1,3}\s+(.+)$/);
    out.push(
      h
        ? <div key={i} className="shopi-rich-heading">{renderInline(h[1], `h${i}`)}</div>
        : <p key={i}>{renderInline(s, `p${i}`)}</p>
    );
  });

  flush();
  return <div className="shopi-rich-text">{out}</div>;
}

const ACTIONS = {
  vendor: ['Add to my vendor', 'Create product listing', 'Create product flyer', 'Use for promotion', 'Search my vendor', 'Search SchoolVerse', 'Edit image', 'Remove background'],
  customer: ['Search SchoolVerse', 'Find similar products', 'Check availability', 'Find vendors'],
  admin: ['Search SchoolVerse', 'Search vendors', 'Edit image'],
  agent: ['Search SchoolVerse', 'Find similar products', 'Edit image']
};
const QUICK = {
  vendor: ['Add a product', 'Show my products', 'Create a flyer', 'Check my vendor'],
  customer: ['Find products', 'Find vendors', 'Show categories'],
  admin: ['Search vendors', 'Find products without images'],
  agent: ['Find vendors', 'Search products']
};

function AttachmentPreview({ item, onRemove, disabled }) {
  return <div className="shopi-attachment"><img src={item.preview} alt={item.file.name} /><div className="shopi-attachment__meta"><b>{item.file.name}</b><span>{Math.round(item.file.size / 1024)} KB</span></div><button disabled={disabled} onClick={onRemove} aria-label="Remove attachment">&#215;</button></div>;
}

function ImageStudio({ image, onClose, onUse }) {
  const canvasRef = useRef(null);
  const [mode, setMode] = useState('clean');
  const [bg, setBg] = useState('#ffffff');
  const [text, setText] = useState('');
  const [price, setPrice] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    const c = canvasRef.current; if (!c || !image) return;
    const ctx = c.getContext('2d'); const img = new Image(); img.onload = () => {
      const max = mode === 'banner' ? 1200 : mode === 'flyer' ? 900 : 1000;
      const scale = Math.min(1, max / img.width);
      c.width = Math.round(img.width * scale); c.height = Math.round(img.height * scale);
      ctx.fillStyle = bg; ctx.fillRect(0, 0, c.width, c.height); ctx.drawImage(img, 0, 0, c.width, c.height);
      if (text || price) { ctx.fillStyle = '#111827'; ctx.font = '700 34px Arial'; ctx.fillText(text, 28, c.height - 72); if (price) { ctx.fillStyle = '#ea580c'; ctx.font = '800 30px Arial'; ctx.fillText(`&#8358;${Number(price).toLocaleString()}`, 28, c.height - 28); } }
    }; img.src = image;
  }, [image, bg, text, price, mode]);
  const exportImage = async () => {
    const c = canvasRef.current; if (!c) return;
    setBusy(true); setError('');
    try { c.toBlob(blob => { if (!blob) throw new Error('Could not create image'); onUse(new File([blob], `shopi-${mode}-${Date.now()}.png`, { type: 'image/png' })); setBusy(false); }, 'image/png', .95); } catch (e) { setError(e.message); setBusy(false); }
  };
  return <div className="shopi-studio-overlay"><div className="shopi-studio">
    <div className="shopi-studio__head"><div><b>Shopi Image Studio</b><span>Make a product image, flyer or banner</span></div><button onClick={onClose}>&#215;</button></div>
    <div className="shopi-studio__canvas"><canvas ref={canvasRef} /></div>
    <div className="shopi-studio__tools"><div className="shopi-studio__modes">{['clean','flyer','banner'].map(x => <button key={x} className={mode === x ? 'active' : ''} onClick={() => setMode(x)}>{x[0].toUpperCase()+x.slice(1)}</button>)}</div>
      <label>Background <input type="color" value={bg} onChange={e => setBg(e.target.value)} /></label>
      <label>Text <input value={text} onChange={e => setText(e.target.value)} placeholder="Optional headline" /></label>
      <label>Price <input value={price} onChange={e => setPrice(e.target.value)} inputMode="numeric" placeholder="Optional" /></label>
      {error && <small className="shopi-error">{error}</small>}
    </div>
    <div className="shopi-studio__foot"><button onClick={onClose}>Cancel</button><button className="primary" disabled={busy} onClick={exportImage}>{busy ? 'Preparing&#8230;' : 'Use this image'}</button></div>
  </div></div>;
}

export default function AIAssistant() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem('shopi_session_id') || null);
  const [input, setInput] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [studioImage, setStudioImage] = useState(null);
  const bodyRef = useRef(null); const fileRef = useRef(null);
  const userRole = role();
  const context = useMemo(() => pageContext(), [open]);

  const loadHistory = useCallback(async () => {
    try { const r = await fetch(`${backendUrl()}/api/ai/sessions?client_id=${encodeURIComponent(clientId())}`, { headers: authHeaders(false) }); if (r.ok) setConversations((await r.json()).sessions || []); } catch (_) {}
  }, []);
  const loadSession = useCallback(async (id) => {
    try { const r = await fetch(`${backendUrl()}/api/ai/sessions/${id}/messages?client_id=${encodeURIComponent(clientId())}`, { headers: authHeaders(false) }); if (!r.ok) return; const d = await r.json(); setSessionId(id); localStorage.setItem('shopi_session_id', id); setMessages((d.messages || []).map(m => ({ role: m.role === 'assistant' ? 'ai' : 'user', text: m.content || '', messageId: m.id, imageUrls: m.image_urls || [] }))); setShowHistory(false); } catch (_) {}
  }, []);
  useEffect(() => { if (open) loadHistory(); }, [open, loadHistory]);
  useEffect(() => { if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight; }, [messages, sending]);
  useEffect(() => () => { attachments.forEach(x => URL.revokeObjectURL(x.preview)); }, [attachments]);

  const addFiles = e => {
    const files = Array.from(e.target.files || []); e.target.value = '';
    const valid = files.filter(f => ['image/jpeg','image/png','image/webp'].includes(f.type) && f.size <= 3.75 * 1024 * 1024);
    setAttachments(prev => {
      const existing = new Set(prev.map(x => `${x.file.name}:${x.file.size}:${x.file.lastModified}`));
      const additions = valid.filter(f => !existing.has(`${f.name}:${f.size}:${f.lastModified}`)).map(f => ({ file: f, preview: URL.createObjectURL(f) }));
      return [...prev, ...additions].slice(0, 6);
    });
  };
  const removeAttachment = i => setAttachments(prev => { URL.revokeObjectURL(prev[i].preview); return prev.filter((_, n) => n !== i); });
  const upload = async file => { const fd = new FormData(); fd.append('file', file); const r = await fetch(`${backendUrl()}/api/ai/upload-image`, { method:'POST', headers: authHeaders(false), body:fd }); const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.detail || `Image upload failed (HTTP ${r.status})`); if (!d.image_url) throw new Error('Image upload completed without an image URL'); return d.image_url; };

  const handleImageAction = async (action, url) => {
    if (!url) return;
    if (/remove background/i.test(action)) {
      setSending(true);
      try {
        const r = await fetch(`${backendUrl()}/api/ai/image/remove-background`, { method:'POST', headers:authHeaders(), body:JSON.stringify({ image_url:url }) });
        const d = await r.json(); if (!r.ok) throw new Error(d.detail || 'Background removal failed');
        setStudioImage(d.image_data_url);
      } catch (e) { setMessages(prev => [...prev, { role:'ai', text:e.message || 'I could not remove the background.' }]); }
      finally { setSending(false); }
      return;
    }
    setStudioImage(url);
  };

  const handleAction = (action, message) => {
    const url = message?.imageUrls?.[0];
    if (/edit image|background|flyer|banner|promotion/i.test(action)) return handleImageAction(action, url);
    return send(action, url ? [url] : null);
  };

  const send = useCallback(async (preset, urlsOverride = null) => {
    const text = String(preset ?? input).trim();
    const overrideUrls = Array.isArray(urlsOverride) ? urlsOverride.filter(Boolean) : null;
    if ((!text && !attachments.length && !overrideUrls?.length) || sending) return;
    setSending(true); setInput('');
    const local = attachments.slice();
    try {
      const urls = overrideUrls || [];
      if (!overrideUrls) for (const item of local) urls.push(await upload(item.file));
      setMessages(prev => [...prev, { role:'user', text, imageUrls: urls }]);
      if (!overrideUrls) { local.forEach(x => URL.revokeObjectURL(x.preview)); setAttachments([]); }
      const r = await fetch(`${backendUrl()}/api/ai/chat`, { method:'POST', headers:authHeaders(), body:JSON.stringify({ message:text, session_id:sessionId, image_urls:urls, client_id:clientId(), context:pageContext() }) });
      const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
      if (d.session_id) { setSessionId(d.session_id); localStorage.setItem('shopi_session_id', d.session_id); }
      setMessages(prev => [...prev, { role:'ai', text:d.reply || 'Done.', messageId:d.message_id, media:d.media || [], productDraft:d.product_draft, actions:d.actions || [], imageUrls:d.image_urls || urls }]);
      loadHistory();
    } catch (e) { setMessages(prev => [...prev, { role:'ai', text:e.message || 'Shopi could not complete that request.' }]); }
    finally { setSending(false); }
  }, [input, attachments, sending, sessionId, loadHistory]);

  const newChat = () => { setSessionId(null); setMessages([]); localStorage.removeItem('shopi_session_id'); setShowHistory(false); };
  const deleteChat = async id => { if (!window.confirm('Delete this conversation?')) return; await fetch(`${backendUrl()}/api/ai/sessions/${id}?client_id=${encodeURIComponent(clientId())}`, { method:'DELETE', headers:authHeaders(false) }); if (id === sessionId) newChat(); loadHistory(); };
  const handleKey = e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } };

  return <>
    {!open && <button className="shopi-fab" onClick={() => setOpen(true)} aria-label="Open Shopi"><span>&#10022;</span></button>}
    {open && <div className="shopi-panel" role="dialog" aria-label="Shopi">
      <header className="shopi-header"><button className="shopi-header__icon" onClick={() => setShowHistory(x=>!x)} title="History">&#9776;</button><div className="shopi-header__avatar">&#10022;</div><div className="shopi-header__identity"><b>Shopi</b><span>{userRole === 'vendor' ? 'Vendor assistant' : 'Shopping assistant'}</span></div><button className="shopi-header__icon" onClick={newChat} title="New chat">&#65291;</button><button className="shopi-header__icon" onClick={() => setOpen(false)} title="Close">&#215;</button></header>
      {showHistory && <aside className="shopi-history"><div className="shopi-history__top"><b>Conversations</b><button onClick={newChat}>New</button></div>{conversations.length ? conversations.map(c => <div key={c.id} className={`shopi-history__item ${c.id===sessionId?'active':''}`}><button onClick={()=>loadSession(c.id)}><b>{c.title || 'Conversation'}</b><span>{new Date(c.updated_at || c.created_at).toLocaleDateString()}</span></button><button onClick={()=>deleteChat(c.id)} aria-label="Delete">&#215;</button></div>) : <p>No previous conversations yet.</p>}</aside>}
      <main className="shopi-body" ref={bodyRef}>
        {!messages.length && <div className="shopi-welcome"><div className="shopi-welcome__icon">&#10022;</div><h3>What can I help you do?</h3><p>Send text, an image, or both. Shopi understands them together with your current SchoolVerse context.</p><div className="shopi-context-pill">{userRole === 'vendor' ? 'Your vendor context is active' : 'Marketplace context is active'}</div></div>}
        {messages.map((m,i) => (
          <div key={`${m.messageId||i}`} className={`shopi-msg shopi-msg--${m.role}`}>
            <div className="shopi-msg__bubble">
              {m.role === 'ai' ? <RichText text={m.text}/> : (
                <>
                  {m.text && <p>{m.text}</p>}
                  {m.imageUrls?.length > 0 && (
                    <div className="shopi-message-images">
                      {m.imageUrls.map((u,n) => <img key={n} src={mediaUrl(u)} alt="Attachment"/>)}
                    </div>
                  )}
                </>
              )}
              {m.role === 'ai' && m.media?.length > 0 && (
                <div className="shopi-media-grid">
                  {m.media.map((x,n) => (
                    <div key={n} className="shopi-media-card">
                      <img src={mediaUrl(x.image_url)} alt={x.name || 'Result'}/>
                      <b>{x.name}</b>
                      {x.price != null && <span>&#8358;{Number(x.price).toLocaleString()}</span>}
                    </div>
                  ))}
                </div>
              )}
              {m.role === 'ai' && (m.actions?.length || 0) > 0 && (
                <div className="shopi-actions">
                  {(m.actions || []).map((a,n) => <button key={n} onClick={() => handleAction(a,m)}>{a}</button>)}
                </div>
              )}
            </div>
          </div>
        ))}
        {sending && <div className="shopi-msg shopi-msg--ai"><div className="shopi-typing"><i/><i/><i/></div></div>}
      </main>
      {!messages.length && <div className="shopi-suggestions">{QUICK[userRole].map(x=><button key={x} onClick={()=>send(x)}>{x}</button>)}</div>}
      {attachments.length>0 && <div className="shopi-attachments">{attachments.map((x,i)=><AttachmentPreview key={`${x.file.name}-${i}`} item={x} onRemove={()=>removeAttachment(i)} disabled={sending}/>)}</div>}
      <footer className="shopi-footer"><button className="shopi-attach" onClick={()=>fileRef.current?.click()} disabled={sending} title="Attach images">&#65291;</button><input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" multiple hidden onChange={addFiles}/><textarea value={input} onChange={e=>setInput(e.target.value)} onKeyDown={handleKey} disabled={sending} placeholder="Ask Shopi&#8230;" rows={1}/><button className="shopi-send" onClick={()=>send()} disabled={sending||(!input.trim()&&!attachments.length)} aria-label="Send">&#10132;</button></footer>
      {studioImage && <ImageStudio image={studioImage} onClose={()=>setStudioImage(null)} onUse={async file=>{ setStudioImage(null); setAttachments([{file,preview:URL.createObjectURL(file)}]); }}/>} 
    </div>}
  </>;
}





